import { Client, Osdk } from "@osdk/client";
import {
  QueryClient,
  useQuery,
  useInfiniteQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import { CustomReport } from "@unruly-osdk/sdk";

const keys = {
  all: ["customReports"] as const,
  byId: (reportId: string) => [...keys.all, "id", reportId] as const,
  infinite: (userId: string) => [...keys.all, "infinite", userId] as const,
};

function CustomReportService(queryClient: QueryClient, apiClient: Client) {
  const service = {
    getCustomReportsForUser: async (
      userId: string,
      pageSize: number = 20,
      nextPageToken?: string,
    ): Promise<{
      data: Osdk.Instance<
        CustomReport,
        never,
        | "id"
        | "reportName"
        | "generatedAt"
        | "version"
        | "type"
        | "subtitle"
        | "periodStart"
        | "periodEnd"
      >[];
      nextPageToken?: string;
    }> => {
      const response = await apiClient(CustomReport)
        .where({ generatedBy: userId })
        .fetchPage({
          $select: [
            "id",
            "reportName",
            "generatedAt",
            "version",
            "type",
            "subtitle",
            "periodStart",
            "periodEnd",
          ],
          $orderBy: { generatedAt: "desc" },
          $pageSize: pageSize,
          ...(nextPageToken && { $nextPageToken: nextPageToken }),
        });

      return {
        data: response.data || [],
        nextPageToken: response.nextPageToken,
      };
    },

    useGetCustomReportsInfinite: (userId: string, pageSize: number) => {
      return useInfiniteQuery({
        queryKey: keys.infinite(userId),
        queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
          return service.getCustomReportsForUser(userId, pageSize, pageParam);
        },
        getNextPageParam: (lastPage) => lastPage.nextPageToken,
        enabled: !!userId,
        initialPageParam: undefined as string | undefined,
      });
    },

    getCustomReportById: async (
      reportId: string,
    ): Promise<Osdk.Instance<CustomReport> | null> => {
      const response = await apiClient(CustomReport)
        .where({ id: reportId })
        .fetchPage();
      return response.data?.[0] || null;
    },

    useGetCustomReportById: (
      reportId: string,
    ): UseQueryResult<Osdk.Instance<CustomReport> | null> => {
      return useQuery({
        queryKey: keys.byId(reportId),
        queryFn: async () => service.getCustomReportById(reportId),
        enabled: !!reportId,
        refetchInterval: (query) => {
          const report = query.state.data;
          if (report && !report.reportAttachmentDocx) return 5000;
          return false;
        },
      });
    },

    invalidateCustomReports: (userId: string) => {
      queryClient.invalidateQueries({
        queryKey: keys.infinite(userId),
      });
    },
  };

  return service;
}

export default CustomReportService;
